import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { Org, OrgUsageSummary } from '@nx-frontend/shared/types';

export interface OrgUsageSummaryStore {
  isLoaded: boolean;
  isFetching: boolean;
  isFailed: boolean;
  data: OrgUsageSummary | null;
}

export const initialOrgUsageSummary: OrgUsageSummaryStore = {
  isLoaded: false,
  isFetching: false,
  isFailed: false,
  data: null,
};

export const $orgUsageSummary = createStore<OrgUsageSummaryStore>(initialOrgUsageSummary);

export const loadOrgUsageSummaryEvent = createEvent<Org>();
const setOrgUsageSummaryFetchingEvent = createEvent();

export const incStorageSizeEvent = createEvent<number>();
export const decStorageSizeEvent = createEvent<number>();

const loadOrgUsageSummaryFx = createEffect(async ({ orgId }: { orgId: string }) => {
  const { data } = await axiosClient.get<OrgUsageSummary>(`${generalEnv.nextApiPath}/orgs/${orgId}/usageSummary`);
  return data;
});

$orgUsageSummary.on(setOrgUsageSummaryFetchingEvent, (store) => {
  if (store.isFetching || store.isLoaded) {
    return store;
  }
  return Object.assign({}, store, {
    isFetching: true,
  });
});

$orgUsageSummary.on(loadOrgUsageSummaryFx.doneData, (orgUsageSummaryStore, data) => {
  return Object.assign({}, orgUsageSummaryStore, {
    data,
    isLoaded: true,
    isFetching: false,
    isFailed: false,
  });
});

$orgUsageSummary.on(loadOrgUsageSummaryFx.failData, (orgUsageSummaryStore, data) => {
  console.error('Fail to fetch org usage summary', data);
  return Object.assign({}, orgUsageSummaryStore, {
    isFetching: false,
    isFailed: true,
  });
});

export const $isOrgUsageSummaryLoaded = $orgUsageSummary.map((orgUsageSummaryStore) => {
  if (!orgUsageSummaryStore) {
    return false;
  }
  return orgUsageSummaryStore.isLoaded;
});

sample({
  source: $orgUsageSummary,
  clock: loadOrgUsageSummaryEvent,
  filter(orgUsageSummaryStore, currentOrganization) {
    if (
      orgUsageSummaryStore.isFetching ||
      orgUsageSummaryStore.isLoaded ||
      !currentOrganization ||
      !currentOrganization.globalId
    ) {
      // do not load if already fetching or loaded
      return false;
    }
    return true;
  },
  fn: (orgUsageSummaryStore, currentOrganization) => {
    return {
      orgId: currentOrganization.globalId,
    };
  },
  target: [loadOrgUsageSummaryFx, setOrgUsageSummaryFetchingEvent],
});

$orgUsageSummary.on(incStorageSizeEvent, (store, size) => {
  const data = store.data;
  if (data && data.currentStorage) {
    return {
      ...store,
      data: {
        ...data,
        currentStorage: data.currentStorage + size,
      },
    };
  }
  return store;
});

$orgUsageSummary.on(decStorageSizeEvent, (store, size) => {
  const data = store.data;
  if (data && data.currentStorage) {
    return {
      ...store,
      data: {
        ...data,
        currentStorage: data.currentStorage - size,
      },
    };
  }
  return store;
});

export const getStorageUsageFx = attach({
  source: $orgUsageSummary,
  effect: (state: OrgUsageSummaryStore) => {
    return {
      maxStorage: state.data?.maxStorage || 0,
      currentStorage: state.data?.currentStorage || 0,
    };
  },
});
